import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

import { JobDetails } from "@/app/Views/FitCheck/FitCheck";

const FITCHECK_BASKET_STORAGE_KEY = "fitcheck_basket";

interface FitCheckBasketItem {
	jobId: string;
	jobDetails: JobDetails;
	addedAt: string;
}

interface FitCheckBasketContextValue {
	basketItems: FitCheckBasketItem[];
	basketCount: number;
	addToBasket: (jobDetails: JobDetails) => void;
	removeFromBasket: (jobId: string) => void;
	clearBasket: () => void;
	isInBasket: (jobId: string) => boolean;
}

const FitCheckBasketContext = createContext<FitCheckBasketContextValue | undefined>(undefined);

function loadBasketFromStorage(): FitCheckBasketItem[] {
	try {
		const stored = localStorage.getItem(FITCHECK_BASKET_STORAGE_KEY);
		if (stored) {
			return JSON.parse(stored);
		}
	} catch (error) {
		console.error("Error loading FitCheck basket from storage:", error);
	}
	return [];
}

function saveBasketToStorage(items: FitCheckBasketItem[]): void {
	try {
		localStorage.setItem(FITCHECK_BASKET_STORAGE_KEY, JSON.stringify(items));
	} catch (error) {
		console.error("Error saving FitCheck basket to storage:", error);
	}
}

export function FitCheckBasketProvider({ children }: { children: React.ReactNode }) {
	const [basketItems, setBasketItems] = useState<FitCheckBasketItem[]>(() => loadBasketFromStorage());

	// Persist basket to localStorage whenever it changes
	useEffect(() => {
		saveBasketToStorage(basketItems);
	}, [basketItems]);

	const addToBasket = useCallback((jobDetails: JobDetails) => {
		setBasketItems((prevItems) => {
			// Check if job is already in basket
			const exists = prevItems.some((item) => item.jobId === jobDetails._id);
			if (exists) {
				return prevItems;
			}

			const newItem: FitCheckBasketItem = {
				jobId: jobDetails._id,
				jobDetails,
				addedAt: new Date().toISOString(),
			};

			return [...prevItems, newItem];
		});
	}, []);

	const removeFromBasket = useCallback((jobId: string) => {
		setBasketItems((prevItems) => prevItems.filter((item) => item.jobId !== jobId));
	}, []);

	const clearBasket = useCallback(() => {
		setBasketItems([]);
	}, []);

	const isInBasket = useCallback(
		(jobId: string) => {
			return basketItems.some((item) => item.jobId === jobId);
		},
		[basketItems],
	);

	const value: FitCheckBasketContextValue = {
		basketItems,
		basketCount: basketItems.length,
		addToBasket,
		removeFromBasket,
		clearBasket,
		isInBasket,
	};

	return <FitCheckBasketContext.Provider value={value}>{children}</FitCheckBasketContext.Provider>;
}

export function useFitCheckBasket() {
	const context = useContext(FitCheckBasketContext);
	if (!context) {
		throw new Error("useFitCheckBasket must be used within a FitCheckBasketProvider");
	}
	return context;
}
